<template>
  <div :class="['shipment-activity-pane-root', { 'is-drag-over': isDragOver }]" @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragEnter" @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop">
    <div class="activity-filter-bar">
      <button type="button" class="activity-time-toggle" :aria-pressed="timeDisplayMode === 'scan' ? 'true' : 'false'"
        :aria-label="`Switch to ${timeDisplayMode === 'local' ? 'local' : 'Voltera'} time display`"
        @click="toggleTimeDisplayMode">
        <span class="activity-time-toggle__track" :class="{ 'is-scan': timeDisplayMode === 'scan' }" aria-hidden="true">
          <span class="activity-time-toggle__thumb"></span>
        </span>
        <span class="activity-time-toggle__label-shell" aria-hidden="true">
          <Transition :name="timeToggleTransitionName" mode="out-in">
            <span :key="timeDisplayMode" class="activity-time-toggle__label">{{ timeToggleLabel }}</span>
          </Transition>
        </span>
      </button>

      <Select v-model="activityFilter" class="activity-filter-select" :options="activityFilterOptions"
        aria-label="Filter activity feed" compact />
    </div>

    <div class="activity-timeline-wrapper">
      <button v-if="showJumpButton" type="button" class="jump-to-bottom" id="jumpToBottom"
        @click="scrollToBottom('smooth')">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      <div ref="timelineRef" class="activity-timeline scroll-content" id="my-scrollbar" :aria-busy="isLoading"
        @click="handleTimelineClick" @scroll="handleTimelineScroll">
        <div v-if="isLoading && visibleTimelineItems.length === 0" class="shipment-activity-empty">Loading activity…</div>
        <div v-else-if="errorMessage" class="shipment-activity-empty shipment-activity-error">{{ errorMessage }}</div>
        <template v-else-if="visibleTimelineItems.length">
          <template v-for="item in visibleTimelineItems" :key="item.key">
            <div v-if="item.kind === 'message' && item.message.displayType === 'context'"
              :class="['timeline-item', 'timeline-item-context', { 'timeline-item-anomaly': isAnomalyAlertMessage(item.message) }]">
              <div class="timeline-date">{{ item.dateLabel }}</div>
              <div class="timeline-time">{{ item.timeLabel }}</div>
              <div class="timeline-context-block">
                <template v-if="isAnomalyAlertMessage(item.message)">
                  <div class="timeline-context-line timeline-anomaly-heading">
                    <span class="icon-hexagon-asterisk-16 timeline-anomaly-icon" aria-hidden="true"></span>
                    <span class="timeline-anomaly-title">System {{ getAnomalyTitle(item.message) }}</span>
                    <span class="timeline-anomaly-priority">· {{ getAnomalyPriorityLabel(item.message) }}</span>
                    <button v-if="isAnomalyActive(item.message)" type="button" class="timeline-anomaly-dismiss"
                      :disabled="dismissingAnomalyMessageId === item.message.id"
                      @click="dismissAnomalyAlert(item.message)">
                      {{ dismissingAnomalyMessageId === item.message.id ? 'Dismissing…' : 'Dismiss' }}
                    </button>
                    <span v-else-if="getAnomalyReviewedByLabel(item.message)" class="timeline-anomaly-silenced">
                      Reviewed by {{ getAnomalyReviewedByLabel(item.message) }}
                    </span>
                  </div>
                  <div class="timeline-context-text timeline-anomaly-summary"
                    v-html="formatAnomalySummaryHtml(item.message)"></div>
                </template>
                <div v-else class="timeline-context-line">
                  <span class="timeline-context-author">{{ getAuthorDisplayName(item.message.authorId) }}</span>
                  <span class="timeline-context-text" v-html="formatContextMessageHtml(item.message)"></span>
                </div>
              </div>
            </div>

            <div v-else-if="item.kind === 'message'"
              :class="['timeline-item', 'user-note', { 'legacy-note': item.message.legacy, 'command-note': item.message.isCommand }]">
              <div class="timeline-date">{{ item.dateLabel }}</div>
              <div class="timeline-time">{{ item.timeLabel }}</div>
              <div class="timeline-message-block">
                <div
                  :class="['timeline-content', { editable: canEditMessage(item.message), editing: editingMessageId === item.message.id }]"
                  @dblclick="startEditingMessage(item.message)">
                  <div class="timeline-header-row">
                    <div class="timeline-header">
                      <img :src="getAuthorAvatar(item.message.authorId)" class="timeline-avatar" />
                      <span class="timeline-user">{{ getAuthorDisplayName(item.message.authorId) }}</span>
                      <span v-if="item.message.legacy" class="timeline-pill legacy">Legacy</span>
                      <span v-else-if="item.message.isCommand" class="timeline-pill command">Command</span>
                    </div>
                    <OverflowMenu v-if="canShowMessageActions(item.message)"
                      :key="`${item.message.id}-${messageMenuRenderKey}`"
                      class="action-menu-wrapper timeline-action-menu" button-label="Message actions" :portal="true"
                      placement="top" :trigger-class="['action-menu-trigger', 'timeline-action-trigger']"
                      :options="getMessageMenuOptions(item.message)"
                      @select="handleMessageMenuSelect($event, item.message)">
                      <template #trigger>
                        <i class="material-icons">more_vert</i>
                      </template>
                    </OverflowMenu>
                  </div>
                  <div class="timeline-message" v-html="formatMessageHtml(item.message)"></div>
                  <div v-if="item.message.attachments && item.message.attachments.length > 0"
                    class="timeline-message-attachments">
                    <button v-for="attachment in item.message.attachments"
                      :key="`${item.message.id}-${attachment.fileId}`" type="button" :class="[
                        'timeline-attachment-card',
                        `is-${getAttachmentPreviewKind(attachment)}`,
                        { 'is-removed': isMessageAttachmentRemoved(attachment) }
                      ]" :aria-disabled="isMessageAttachmentRemoved(attachment) ? 'true' : 'false'"
                      :title="getMessageAttachmentStatusLabel(attachment)"
                      @click.stop="openMessageAttachment(attachment)">
                      <img v-if="!isMessageAttachmentRemoved(attachment) && attachmentPreviewUrls[attachment.fileId]"
                        :src="attachmentPreviewUrls[attachment.fileId]" :alt="`${attachment.displayName} preview`"
                        class="timeline-attachment-image" loading="lazy" />
                      <span v-else class="timeline-attachment-label">{{ isMessageAttachmentRemoved(attachment) ?
                        'REMOVED' :
                        getAttachmentPreviewLabel(attachment) }}</span>
                      <span class="timeline-attachment-name">{{ attachment.displayName }}</span>
                    </button>
                  </div>
                </div>
                <div v-if="item.message.edited" class="timeline-message-meta">
                  <span v-if="item.message.edited">(edited)</span>
                </div>
              </div>
            </div>

            <div v-else class="scan-group-container">
              <div class="scan-group-box">
                <div v-if="item.location" class="scan-location-header">
                  <span
                    :class="['timeline-location', { editable: Boolean(item.waypointId), verified: item.verified, 'has-coords': Boolean(item.coords) }]"
                    :title="item.waypointId ? 'Click to view on map • Double-click to edit waypoint' : (item.coords ? 'Click to view on map' : '')"
                    @click="handleLocationClick(item.coords)"
                    @dblclick.prevent.stop="handleWaypointEdit(item.waypointId)">
                    {{ item.location }}
                  </span>
                </div>

                <div class="scan-group">
                  <div v-for="scan in item.scans" :key="scan.key" class="scan-item">
                    <div class="scan-date">{{ scan.dateLabel }}</div>
                    <div class="scan-time">{{ scan.timeLabel }}</div>
                    <div class="scan-description">
                      <span v-if="scan.delayed" class="icon-alert-triangle-16 icon-orange scan-delay-icon"
                        data-tippy-content="Delayed" data-tippy-placement="top" aria-label="Delayed"></span>
                      <span>{{ scan.description }}</span>
                      <div v-if="scan.exceptionDescription" class="scan-exception">{{ scan.exceptionDescription }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
        <div v-else class="shipment-activity-empty">{{ activityEmptyLabel }}</div>
      </div>
    </div>

    <div v-if="isDragOver" class="shipment-activity-drop-panel">
      <div class="shipment-activity-drop-copy">
        <span>Drop files to attach</span>
        <small>Supported: images, PDF, XLSX, CSV, TXT. Max 10 MB each. Up to 3 attachments per message.</small>
      </div>
    </div>

    <div class="shipment-message-progress-slot">
      <Transition name="shipment-message-progress">
        <div v-if="isMessageActionPending || messageActionError" class="shipment-message-progress"
          :class="{ 'is-error': messageActionError }" :role="isMessageActionPending ? 'progressbar' : undefined"
          :aria-label="isMessageActionPending ? 'Updating message' : undefined">
          <span></span>
        </div>
      </Transition>
    </div>
    <div v-if="messageActionError" class="shipment-activity-notice shipment-activity-error" role="alert">{{
      messageActionError }}</div>

    <MessageComposer ref="composerRef" v-model="draft" placeholder="Add interaction notes"
      :avatar-url="currentUserAvatar" :sending="isSending" :send-disabled="isSendDisabled" :is-editing="isEditing"
      :disabled="isSending" :force-expanded="forceComposerExpanded"
      :has-attachments="draftAttachments.length > 0 || uploadingDraftAttachments.length > 0"
      :can-attach="canAttachFiles" :highlight-html="composerHighlightHtml" :plus-menu-key="composerMenuRenderKey"
      :plus-menu-options="composerMenuOptions" @submit="submitMessage" @cancel="cancelEditingMessage"
      @mention="handleComposerMentionAction" @link="handleComposerShipmentLinkAction"
      @attach="handleComposerPaperclipAction" @upload="handleComposerMenuUpload" @input="handleComposerInput"
      @focus="handleComposerFocus" @blur="handleComposerBlur" @keydown="handleComposerKeydown"
      @selection-change="handleComposerSelectionChange" @plus-open="handleComposerPlusMenuOpen">
      <template #attachments>
        <div v-for="attachment in draftAttachments" :key="`draft-${attachment.fileId}`"
          :class="['composer-attachment-card', `is-${getAttachmentPreviewKind(attachment)}`]">
          <img v-if="attachmentPreviewUrls[attachment.fileId]" :src="attachmentPreviewUrls[attachment.fileId]"
            :alt="`${attachment.displayName} preview`" class="composer-attachment-image" loading="lazy" />
          <span v-else class="composer-attachment-label">{{ getAttachmentPreviewLabel(attachment) }}</span>
          <button type="button" class="composer-attachment-remove" :aria-label="`Remove ${attachment.displayName}`"
            @click.stop="removeDraftAttachment(attachment.fileId)">
            <i class="material-icons">close</i>
          </button>
        </div>
        <div v-for="attachment in uploadingDraftAttachments" :key="attachment.key"
          :class="['composer-attachment-card', 'uploading', `is-${attachment.previewKind}`]">
          <img v-if="attachment.previewUrl" :src="attachment.previewUrl" :alt="`${attachment.displayName} preview`"
            class="composer-attachment-image" loading="lazy" />
          <span v-else class="composer-attachment-label">{{ attachment.previewLabel }}</span>
          <div class="composer-attachment-progress-shell" aria-hidden="true">
            <div class="composer-attachment-progress-bar" :style="{ width: `${attachment.progressPercent}%` }"></div>
          </div>
        </div>
      </template>

      <template #suggestions>
        <div v-if="composerSuggestions.length" class="composer-suggestion-list" role="listbox"
          aria-label="Message suggestions">
          <button v-for="(suggestion, index) in composerSuggestions" :key="suggestion.key" type="button"
            :class="['composer-suggestion-item', { active: index === highlightedSuggestionIndex, shipment: suggestion.kind === 'shipment' }]"
            @mouseenter="highlightedSuggestionIndex = index" @mousedown.prevent="applyComposerSuggestion(index)">
            <img v-if="suggestion.kind === 'mention'" :src="suggestion.avatar" class="composer-suggestion-avatar" />
            <div v-else-if="suggestion.kind === 'file'" class="composer-suggestion-file">{{ suggestion.previewLabel }}
            </div>
            <div v-else-if="suggestion.kind === 'file-upload'" class="composer-suggestion-file upload">
              <i class="material-icons">upload_file</i>
            </div>
            <div v-else class="composer-suggestion-hash">#</div>
            <div class="composer-suggestion-line">
              <span class="composer-suggestion-label">{{ suggestion.label }}</span>
              <span v-if="suggestion.secondary" class="composer-suggestion-separator">&rsaquo;</span>
              <span v-if="suggestion.secondary" class="composer-suggestion-secondary">{{ suggestion.secondary }}</span>
            </div>
          </button>
        </div>
      </template>
    </MessageComposer>
    <input ref="attachmentInputRef" type="file" class="composer-hidden-input" multiple
      @change="handleAttachmentInputChange" />
    <div v-if="noticeMessage" class="shipment-activity-notice">{{ noticeMessage }}</div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { getCurrentUser } from '@/modules/firebase/Auth';
import {
  type MessageAttachment,
  type MessageAttachmentCandidate,
  messageClient,
  type MessageEntityCandidates,
  type MessageMentionCandidate,
  type MessageRecord,
  type MessageShipmentReferenceCandidate
} from '@/modules/common/messaging/MessageClient';
import { subscribeMessageFeed } from '@/modules/common/messaging/MessageFeed';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  findDuplicateRecordFileUpload,
  getRecordFilePreviewKind,
  getRecordFilePreviewLabel,
  uploadRecordFiles,
  validateLocalRecordFile,
  type RecordFilePreviewKind
} from '@/modules/common/files/RecordFileWorkflow';
import { db, functions } from '@/modules/firebase';
import { UserStore } from '@/modules/users/UserStore';
import { moment } from '@/modules/shipments/utils/date-time';
import {
  Button,
  MessageComposer,
  OverflowMenu,
  Select,
  type MessageComposerExpose,
  type OverflowMenuOption,
  type SelectOption
} from '@/components/ui';
import { onDocumentModalEvent } from '@/components/modal-events';

import { registerShipmentActivityController, type ShipmentActivityController, type ShipmentActivityOpenContext } from './activity-controller';

type ActivityFilterMode = 'all' | 'events' | 'messages' | 'alerts';
type TimeDisplayMode = 'local' | 'scan';

const ANOMALY_SYSTEM_AUTHOR_ID = 'system';

interface DismissedAnomalyState {
  active: boolean;
  title?: string | null;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  messageId?: string | null;
  clearedByName?: string | null;
}

const dismissShipmentAnomalyCallable = httpsCallable<{ tracking: string }, { status: string; anomaly: DismissedAnomalyState | null }>(
  functions,
  'dismissShipmentAnomaly'
);

interface ScanTimelineItem {
  kind: 'scan';
  key: string;
  timestamp: number;
  description: string;
  exceptionDescription?: string;
  delayed: boolean;
  location: string;
  timezone: string | null;
  waypointId: string | null;
  verified: boolean;
  coords: [number, number] | null;
}

interface MessageTimelineItem {
  kind: 'message';
  key: string;
  timestamp: number;
  message: MessageRecord;
}

interface TimelineMessageView {
  kind: 'message';
  key: string;
  dateLabel: string;
  timeLabel: string;
  timestamp: number;
  message: MessageRecord;
}

interface TimelineScanView {
  key: string;
  dateLabel: string;
  timeLabel: string;
  description: string;
  exceptionDescription?: string;
  delayed: boolean;
}

interface TimelineScanGroupView {
  kind: 'scan-group';
  key: string;
  timestamp: number;
  location: string;
  waypointId: string | null;
  verified: boolean;
  coords: [number, number] | null;
  scans: TimelineScanView[];
}

type TimelineViewItem = TimelineMessageView | TimelineScanGroupView;

type ComposerSuggestion = MentionComposerSuggestion | ShipmentComposerSuggestion | FileComposerSuggestion | UploadFileComposerSuggestion;

interface ComposerTokenContext {
  kind: 'mention' | 'shipment' | 'file';
  query: string;
  start: number;
  end: number;
}

type ForcedComposerSuggestionKind = ComposerTokenContext['kind'];

interface ShipmentSuggestionLoadOptions {
  showFullList?: boolean;
  resultLimit?: number;
}

interface MentionComposerSuggestion {
  kind: 'mention';
  key: string;
  uid: string;
  label: string;
  secondary: string;
  avatar: string;
}

interface ShipmentComposerSuggestion {
  kind: 'shipment';
  key: string;
  tracking: string;
  canonicalTracking: string;
  label: string;
  secondary: string;
}

interface FileComposerSuggestion {
  kind: 'file';
  key: string;
  fileId: string;
  label: string;
  secondary: string;
  previewLabel: string;
  attachment: MessageAttachment;
}

interface UploadFileComposerSuggestion {
  kind: 'file-upload';
  key: string;
  label: string;
  secondary: string;
}

interface UploadingDraftAttachment {
  key: string;
  displayName: string;
  previewKind: RecordFilePreviewKind;
  previewLabel: string;
  previewUrl: string | null;
  progressPercent: number;
}

function timestampToMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof maybeTimestamp.toMillis === 'function') {
      return maybeTimestamp.toMillis();
    }

    const seconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds;
    const nanoseconds = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return (seconds * 1000) + Math.floor(nanoseconds / 1_000_000);
    }
  }

  return 0;
}

function normalizeSnapshotFile(id: string, value: any): RecordFile {
  return {
    id,
    recordType: value.recordType,
    recordId: value.recordId,
    displayName: value.displayName,
    originalName: value.originalName,
    mimeType: value.mimeType,
    extension: value.extension,
    sizeBytes: Number(value.sizeBytes || 0),
    status: value.status,
    deleted: value.deleted === true,
    createdBy: value.createdBy,
    createdAt: timestampToMillis(value.createdAt),
    updatedAt: timestampToMillis(value.updatedAt),
    storagePath: value.storagePath,
    version: Number(value.version || 1)
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function formatPlainMessageHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function formatPlainComposerHtml(value: string): string {
  return escapeHtml(value)
    .replace(/\n$/g, '\n\u200b')
    .replace(/\n/g, '<br>')
    .replace(/ {2}/g, ' &nbsp;');
}

function canonicalizeTrackingValue(value: string): string {
  return value.toUpperCase().replace(/[\s\-.]/g, '');
}

function getMentionInsertionLabel(label: string): string {
  return label.startsWith('@') ? label : `@${label}`;
}

function getShipmentInsertionLabel(label: string): string {
  return label.startsWith('#') ? label : `#${label}`;
}

function buildLocationLabel(scan: any): string {
  if (!scan?.address) {
    return '';
  }

  const city = scan.address.city?.trim();
  const country = scan.address.countrycode?.trim();

  if (city && country) {
    return `${city}, ${country}`;
  }

  return city || country || '';
}

function resolveWaypointVerified(record: any, waypointId: string | null | undefined): boolean {
  if (!waypointId || !record?.waypointIndex) return false;

  const { origin, destination, progress } = record.waypointIndex;
  if (origin?.id === waypointId) return origin.verified === true;
  if (destination?.id === waypointId) return destination.verified === true;

  const progressWaypoint = (progress || []).find((entry: any) => entry?.id === waypointId);
  return progressWaypoint?.verified === true;
}

const DEFAULT_USER_TIMEZONE = 'America/New_York';

function formatTimelineDayKey(timestamp: number, timezone: string): string {
  return moment.unix(timestamp).tz(timezone).format('YYYY-MM-DD');
}

function formatTimelineDateLabel(timestamp: number, timezone: string): string {
  return moment.unix(timestamp).tz(timezone).format('MMM DD');
}

function formatTimelineTimeLabel(timestamp: number, timezone: string): string {
  return moment.unix(timestamp).tz(timezone).format('HH:mm');
}

function resolveMessageTimezone(): string {
  return DEFAULT_USER_TIMEZONE;
}

function resolveScanTimezone(scanTimezone: string | null | undefined): string {
  if (typeof scanTimezone === 'string' && scanTimezone.trim().length > 0) {
    return scanTimezone.trim();
  }

  return DEFAULT_USER_TIMEZONE;
}

export default defineComponent({
  name: 'ShipmentActivityPane',
  components: {
    Button,
    MessageComposer,
    OverflowMenu,
    Select
  },
  setup() {
    const tracking = ref('');
    const record = ref<any | null>(null);
    const messages = ref<MessageRecord[]>([]);
    const draft = ref('');
    const draftAttachments = ref<MessageAttachment[]>([]);
    const uploadingDraftAttachments = ref<UploadingDraftAttachment[]>([]);
    const isLoading = ref(false);
    const isSending = ref(false);
    const isUploadingAttachments = ref(false);
    const isDragOver = ref(false);
    const deletingMessageId = ref('');
    const dismissingAnomalyMessageId = ref('');
    const editingMessageId = ref('');
    const errorMessage = ref('');
    const messageActionError = ref('');
    const showJumpButton = ref(false);
    const timelineRef = ref<HTMLElement | null>(null);
    const composerRef = ref<MessageComposerExpose | null>(null);
    const attachmentInputRef = ref<HTMLInputElement | null>(null);
    const userStoreVersion = ref(0);
    const composerSuggestions = ref<ComposerSuggestion[]>([]);
    const forcedComposerSuggestionKind = ref<ForcedComposerSuggestionKind | null>(null);
    const timeDisplayMode = ref<TimeDisplayMode>('local');
    const timeToggleDirection = ref<'forward' | 'backward'>('forward');
    const activityFilter = ref<ActivityFilterMode>('all');
    const highlightedSuggestionIndex = ref(0);
    const noticeMessage = ref('');
    const attachmentPreviewUrls = ref<Record<string, string>>({});
    const attachmentPreviewSignatures = ref<Record<string, string>>({});
    const referencedFileStateById = ref<Record<string, { status: string; deleted: boolean }>>({});
    const hasLoadedReferencedFiles = ref(false);
    const readyFiles = ref<RecordFile[]>([]);
    const dismissedComposerTokenKey = ref('');
    const selectedEntities = ref<MessageEntityCandidates>({ mentions: [], shipments: [] });
    const composerMenuRenderKey = ref(0);
    const messageMenuRenderKey = ref(0);
    let activeRequestId = 0;
    let activeSuggestionRequestId = 0;
    let liveMessagesUnsubscribe: (() => void) | null = null;
    let referencedFilesUnsubscribe: (() => void) | null = null;
    let removeModalShownListener: (() => void) | null = null;
    const readReceiptReconciliation = new Map<string, number>();
    const shipmentSuggestionCache = new Map<string, ShipmentComposerSuggestion[]>();
    const pendingAttachmentPreviewIds = new Set<string>();

    const currentUser = computed(() => getCurrentUser());
    const currentUserId = computed(() => currentUser.value?.uid || '');
    const currentUserAvatar = computed(() => {
      const cached = currentUser.value;

      return cached?.slack?.avatar
        || cached?.photoURL
        || UserStore.getAvatar(cached?.uid || cached?.email || cached?.displayName)
        || '/assets/img/avatar.png';
    });

    const currentUserDisplayName = computed(() => {
      const cached = currentUser.value;

      return cached?.slack?.display_name
        || cached?.displayName
        || cached?.email
        || UserStore.getDisplayName(cached?.uid || cached?.email || cached?.displayName)
        || 'User';
    });

    const isSendDisabled = computed(() => {
      return isSending.value
        || isUploadingAttachments.value
        || !tracking.value
        || !currentUserId.value
        || (draft.value.trim().length === 0 && draftAttachments.value.length === 0);
    });

    const composerMenuOptions = computed<OverflowMenuOption[]>(() => [{
      key: 'upload',
      label: 'Upload file',
      icon: 'upload_file'
    }]);

    function getComposerElement(): HTMLTextAreaElement | null {
      return composerRef.value?.getTextareaElement() || null;
    }

    function syncComposerLayoutNextTick(): void {
      composerRef.value?.syncLayout();
    }

    function scheduleComposerLayoutSync(): void {
      syncComposerLayoutNextTick();
    }

    function clearScheduledComposerLayout(): void {
      // Layout scheduling is owned by MessageComposer.
    }

    const canAttachFiles = computed(() => Boolean(tracking.value) && !isSending.value && !isUploadingAttachments.value && !isEditing.value);

    const isEditing = computed(() => editingMessageId.value.length > 0);
    const isMessageActionPending = computed(() => isSending.value || Boolean(deletingMessageId.value));

    const forceComposerExpanded = computed(() => (
      isSending.value
      || composerSuggestions.value.length > 0
      || Boolean(forcedComposerSuggestionKind.value)
      || isUploadingAttachments.value
    ));

    function expandComposer(): void {
      composerRef.value?.expand();
      syncComposerLayoutNextTick();
    }

    function collapseComposer(): void {
      composerRef.value?.collapse();
    }

    function handleComposerFocus(): void {
      expandComposer();
    }

    function handleComposerBlur(): void {
      // Collapse behavior is owned by MessageComposer.
    }

    function handleComposerPlusMenuOpen(): void {
      expandComposer();
    }

    const composerHighlightHtml = computed(() => {
      const messageText = draft.value;
      if (!messageText) {
        return '';
      }

      const renderableEntities = getRenderableComposerEntities(messageText);
      if (renderableEntities.length === 0) {
        return formatPlainComposerHtml(messageText);
      }

      let cursor = 0;
      let html = '';

      for (const entity of renderableEntities) {
        if (entity.start < cursor || entity.end > messageText.length || entity.end <= entity.start) {
          continue;
        }

        html += formatPlainComposerHtml(messageText.slice(cursor, entity.start));
        const entityText = messageText.slice(entity.start, entity.end);
        const entityClass = entity.kind === 'mention'
          ? 'composer-entity composer-entity-mention'
          : 'composer-entity composer-entity-shipment';

        html += `<span class="${entityClass}">${formatPlainComposerHtml(entityText)}</span>`;
        cursor = entity.end;
      }

      html += formatPlainComposerHtml(messageText.slice(cursor));
      return html;
    });

    const timelineItems = computed<TimelineViewItem[]>(() => {
      const useScanTimezone = timeDisplayMode.value === 'scan';
      const waypointTimezones = new Map<string, string>(
        (record.value?.waypointIndex?.progress || [])
          .filter((waypoint: any) => (
            typeof waypoint?.id === 'string'
            && typeof waypoint?.timezone === 'string'
            && waypoint.timezone.trim().length > 0
          ))
          .map((waypoint: any) => [waypoint.id, waypoint.timezone.trim()])
      );
      const scans: ScanTimelineItem[] = (record.value?.progress || []).map((scan: any, index: number) => ({
        kind: 'scan',
        key: `scan-${scan.timestamp || 0}-${scan.waypointId || index}`,
        timestamp: Number(scan.timestamp || 0),
        description: scan?.description || scan?.eventDescription || '',
        exceptionDescription: typeof scan?.exceptionDescription === 'string' && scan.exceptionDescription.trim().length > 5
          ? scan.exceptionDescription.trim()
          : undefined,
        delayed: scan?.delayed === true,
        location: buildLocationLabel(scan),
        timezone: waypointTimezones.get(scan?.waypointId)
          || (typeof scan?.location?.timezone === 'string' && scan.location.timezone.trim().length > 0
            ? scan.location.timezone.trim()
            : null),
        waypointId: scan?.waypointId || null,
        verified: resolveWaypointVerified(record.value, scan?.waypointId || null),
        coords: Array.isArray(scan?.location?.geocode)
          ? scan.location.geocode
          : (Array.isArray(scan?.location?.coords) ? scan.location.coords : null)
      }));

      const messageItems: MessageTimelineItem[] = messages.value.map(message => ({
        kind: 'message',
        key: `message-${message.id}`,
        timestamp: Number(message.timestamp || 0),
        message
      }));

      const sortedItems = [...scans, ...messageItems].sort((left, right) => left.timestamp - right.timestamp);
      const groupedItems: Array<MessageTimelineItem | { kind: 'scan-group'; timestamp: number; location: string; waypointId: string | null; verified: boolean; coords: [number, number] | null; scans: ScanTimelineItem[]; }> = [];
      let currentScanGroup: { kind: 'scan-group'; timestamp: number; location: string; waypointId: string | null; verified: boolean; coords: [number, number] | null; scans: ScanTimelineItem[]; } | null = null;

      sortedItems.forEach(item => {
        if (item.kind === 'scan') {
          if (
            currentScanGroup
            && currentScanGroup.location === item.location
            && currentScanGroup.waypointId === item.waypointId
          ) {
            currentScanGroup.scans.push(item);
            return;
          }

          if (currentScanGroup) {
            groupedItems.push(currentScanGroup);
          }

          currentScanGroup = {
            kind: 'scan-group',
            timestamp: item.timestamp,
            location: item.location,
            waypointId: item.waypointId,
            verified: item.verified,
            coords: item.coords,
            scans: [item]
          };
          return;
        }

        if (currentScanGroup) {
          groupedItems.push(currentScanGroup);
          currentScanGroup = null;
        }

        groupedItems.push(item);
      });

      if (currentScanGroup) {
        groupedItems.push(currentScanGroup);
      }

      let previousRenderedDay: string | null = null;

      return groupedItems.map(item => {
        if (item.kind === 'message') {
          const messageTimezone = resolveMessageTimezone();
          const renderedDay = formatTimelineDayKey(item.timestamp, messageTimezone);
          const viewItem: TimelineMessageView = {
            kind: 'message',
            key: item.key,
            timestamp: item.timestamp,
            dateLabel: formatTimelineDateLabel(item.timestamp, messageTimezone),
            timeLabel: formatTimelineTimeLabel(item.timestamp, messageTimezone),
            message: item.message
          };

          previousRenderedDay = renderedDay;
          return viewItem;
        }

        const scans = item.scans.map((scan, index) => {
          const scanTimezone = useScanTimezone
            ? resolveScanTimezone(scan.timezone)
            : resolveMessageTimezone();
          const renderedDay = formatTimelineDayKey(scan.timestamp, scanTimezone);
          const dateLabel = index === 0 || previousRenderedDay !== renderedDay
            ? formatTimelineDateLabel(scan.timestamp, scanTimezone)
            : '';

          previousRenderedDay = renderedDay;

          return {
            key: scan.key,
            dateLabel,
            timeLabel: formatTimelineTimeLabel(scan.timestamp, scanTimezone),
            description: scan.description,
            exceptionDescription: scan.exceptionDescription,
            delayed: scan.delayed
          };
        });

        return {
          kind: 'scan-group',
          key: `scan-group-${item.timestamp}-${item.waypointId || item.location || 'unknown'}`,
          timestamp: item.timestamp,
          location: item.location,
          waypointId: item.waypointId,
          verified: item.verified,
          coords: item.coords,
          scans
        } satisfies TimelineScanGroupView;
      });
    });

    function isAnomalyAlertMessage(message: MessageRecord): boolean {
      return message.displayType === 'context'
        && message.authorId === ANOMALY_SYSTEM_AUTHOR_ID
        && (Boolean(message.anomaly) || message.id.startsWith('anomaly_') || message.text.includes('Anomaly detected'));
    }

    function getLinkedAnomalyState(message: MessageRecord): DismissedAnomalyState | null {
      const anomaly = record.value?.anomaly as DismissedAnomalyState | undefined;
      return anomaly?.messageId === message.id ? anomaly : null;
    }

    function getAnomalyTitle(message: MessageRecord): string {
      return message.anomaly?.title?.trim() || getLinkedAnomalyState(message)?.title?.trim() || 'Shipment Alert';
    }

    function getAnomalyPriorityLabel(message: MessageRecord): string {
      const priority = message.anomaly?.priority
        || getLinkedAnomalyState(message)?.priority
        || /\b(HIGH|MEDIUM|LOW) priority\b/i.exec(message.text)?.[1]?.toUpperCase();
      return priority === 'HIGH' ? 'P1' : priority === 'MEDIUM' ? 'P2' : 'P3';
    }

    function isAnomalyActive(message: MessageRecord): boolean {
      return (message.anomaly?.active ?? getLinkedAnomalyState(message)?.active) !== false;
    }

    function getAnomalyDismissedByName(message: MessageRecord): string | null {
      return message.anomaly?.dismissedByName || getLinkedAnomalyState(message)?.clearedByName || null;
    }

    function formatReviewedByName(displayName: string): string {
      const parts = displayName.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) {
        return displayName.trim();
      }

      if (parts.length === 1) {
        return parts[0];
      }

      const firstName = parts[0];
      const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${firstName} ${lastInitial}.`;
    }

    function getAnomalyReviewedByLabel(message: MessageRecord): string | null {
      const displayName = getAnomalyDismissedByName(message);
      return displayName ? formatReviewedByName(displayName) : null;
    }

    async function dismissAnomalyAlert(message: MessageRecord): Promise<void> {
      if (!tracking.value || !isAnomalyActive(message) || dismissingAnomalyMessageId.value) {
        return;
      }

      dismissingAnomalyMessageId.value = message.id;
      errorMessage.value = '';
      try {
        const result = await dismissShipmentAnomalyCallable({ tracking: tracking.value });
        const anomaly = result.data.anomaly;
        if (!anomaly) {
          return;
        }

        record.value = record.value ? { ...record.value, anomaly } : record.value;
        messages.value = messages.value.map(existingMessage => existingMessage.id === message.id
          ? {
            ...existingMessage,
            anomaly: {
              ...existingMessage.anomaly,
              title: anomaly.title || existingMessage.anomaly?.title,
              priority: anomaly.priority || existingMessage.anomaly?.priority,
              active: anomaly.active,
              dismissedByName: anomaly.clearedByName || currentUserDisplayName.value
            }
          }
          : existingMessage);
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to dismiss alert';
      } finally {
        dismissingAnomalyMessageId.value = '';
      }
    }

    const hasActivityAlerts = computed(() => (
      timelineItems.value.some(item => {
        if (item.kind === 'scan-group') {
          return item.scans.some(scan => scan.delayed);
        }

        return isAnomalyAlertMessage(item.message);
      })
    ));

    const activityFilterOptions = computed<SelectOption[]>(() => {
      const options: SelectOption[] = [
        { value: 'all', label: 'Show all' },
        { value: 'events', label: 'Events only' },
        { value: 'messages', label: 'Messages only' }
      ];

      if (hasActivityAlerts.value) {
        options.push({ value: 'alerts', label: 'Alerts only' });
      }

      return options;
    });

    const visibleTimelineItems = computed<TimelineViewItem[]>(() => {
      if (activityFilter.value === 'all') {
        return timelineItems.value;
      }

      if (activityFilter.value === 'alerts') {
        return timelineItems.value.flatMap((item): TimelineViewItem[] => {
          if (item.kind === 'message') {
            return isAnomalyAlertMessage(item.message) ? [item] : [];
          }

          const delayedScans = item.scans.filter(scan => scan.delayed);
          if (delayedScans.length === 0) {
            return [];
          }

          return [{
            ...item,
            key: `${item.key}-alerts`,
            scans: delayedScans
          }];
        });
      }

      return timelineItems.value.filter(item => {
        if (activityFilter.value === 'events') {
          if (item.kind === 'scan-group') {
            return true;
          }

          return item.kind === 'message' && item.message.displayType === 'context';
        }

        return item.kind === 'message' && item.message.displayType !== 'context';
      });
    });

    watch(hasActivityAlerts, (hasAlerts) => {
      if (!hasAlerts && activityFilter.value === 'alerts') {
        activityFilter.value = 'all';
      }
    });

    const timeToggleLabel = computed(() => (
      timeDisplayMode.value === 'local' ? 'Voltera Time' : 'Local Time'
    ));

    const timeToggleTransitionName = computed(() => (
      timeToggleDirection.value === 'forward' ? 'activity-time-forward' : 'activity-time-backward'
    ));

    const activityEmptyLabel = computed(() => {
      if (activityFilter.value !== 'all' && timelineItems.value.length > 0) {
        return 'No matching activity.';
      }

      return 'No activity yet.';
    });

    function toggleTimeDisplayMode(): void {
      timeToggleDirection.value = timeDisplayMode.value === 'local' ? 'forward' : 'backward';
      timeDisplayMode.value = timeDisplayMode.value === 'local' ? 'scan' : 'local';
    }

    async function ensureUserStoreReady(): Promise<void> {
      if (UserStore.isInitialized()) {
        if (userStoreVersion.value === 0) {
          userStoreVersion.value += 1;
        }
        return;
      }

      try {
        await UserStore.init();
      } catch (error) {
        console.warn('[ShipmentActivityPane] UserStore initialization failed:', error);
      } finally {
        userStoreVersion.value += 1;
      }
    }

    function getAuthorDisplayName(identifier: string): string {
      void userStoreVersion.value;

      if (identifier && identifier === currentUserId.value) {
        return currentUserDisplayName.value;
      }

      return UserStore.getDisplayName(identifier);
    }

    function getAuthorAvatar(identifier: string): string {
      void userStoreVersion.value;

      if (identifier && identifier === currentUserId.value) {
        return currentUserAvatar.value;
      }

      return UserStore.getAvatar(identifier);
    }

    function formatMessageHtml(message: MessageRecord): string {
      const messageText = message.text || message.lines.join('\n');
      const renderableEntities = [
        ...(message.entities?.mentions || []).map(mention => ({
          kind: 'mention' as const,
          start: mention.start,
          end: mention.end,
          label: mention.label,
          uid: mention.uid
        })),
        ...(message.entities?.shipments || []).map(shipment => ({
          kind: 'shipment' as const,
          start: shipment.start,
          end: shipment.end,
          label: shipment.label,
          canonicalTracking: shipment.canonicalTracking
        }))
      ]
        .filter(entity => Number.isInteger(entity.start) && Number.isInteger(entity.end) && entity.start !== null && entity.end !== null)
        .sort((left, right) => (left.start as number) - (right.start as number));

      if (renderableEntities.length === 0) {
        return formatPlainMessageHtml(messageText);
      }

      let cursor = 0;
      let html = '';

      for (const entity of renderableEntities) {
        const start = entity.start as number;
        const end = entity.end as number;

        if (start < cursor || end > messageText.length || end <= start) {
          continue;
        }

        html += formatPlainMessageHtml(messageText.slice(cursor, start));
        const entityText = messageText.slice(start, end);

        if (entity.kind === 'mention') {
          html += `<span class="timeline-entity timeline-entity-mention" data-entity-type="mention" data-uid="${escapeAttribute(entity.uid)}">${formatPlainMessageHtml(entityText)}</span>`;
        } else {
          const shipmentUrl = `/shipments/${encodeURIComponent(entity.canonicalTracking)}`;
          html += `<a href="${escapeAttribute(shipmentUrl)}" class="timeline-entity timeline-entity-shipment" data-entity-type="shipment" data-tracking="${escapeAttribute(entity.canonicalTracking)}">${formatPlainMessageHtml(entityText)}</a>`;
        }

        cursor = end;
      }

      html += formatPlainMessageHtml(messageText.slice(cursor));
      return html;
    }

    function sanitizeAnomalySummaryText(text: string): string {
      return text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => {
          if (!line) {
            return false;
          }

          if (/anomaly detected/i.test(line)) {
            return false;
          }

          if (/^reply to clear this (alert|shipment)\.?$/i.test(line)) {
            return false;
          }

          return true;
        })
        .join('\n')
        .trim();
    }

    function formatAnomalySummaryHtml(message: MessageRecord): string {
      const messageText = sanitizeAnomalySummaryText(message.text || message.lines.join('\n'));
      return formatPlainMessageHtml(messageText);
    }

    function formatContextMessageHtml(message: MessageRecord): string {
      const messageText = message.text || message.lines.join('\n');
      return formatPlainMessageHtml(messageText);
    }

    function canEditMessage(message: MessageRecord): boolean {
      return message.actionState?.canEdit === true;
    }

    function canDeleteMessage(message: MessageRecord): boolean {
      return message.actionState?.canDelete === true;
    }

    function canShowMessageActions(message: MessageRecord): boolean {
      return canEditMessage(message) || canDeleteMessage(message);
    }

    function getAttachmentPreviewKind(attachment: MessageAttachment) {
      return getRecordFilePreviewKind(attachment);
    }

    function getAttachmentPreviewLabel(attachment: MessageAttachment): string {
      return getRecordFilePreviewLabel(attachment);
    }

    function buildAttachmentSignature(attachment: MessageAttachment): string {
      return `${attachment.storagePath}|${attachment.version}`;
    }

    function isMessageAttachmentRemoved(attachment: MessageAttachment): boolean {
      if (!hasLoadedReferencedFiles.value) {
        return false;
      }

      const fileState = referencedFileStateById.value[attachment.fileId];
      if (!fileState) {
        return true;
      }

      return fileState.deleted === true || fileState.status === 'deleted';
    }

    function getMessageAttachmentStatusLabel(attachment: MessageAttachment): string {
      return isMessageAttachmentRemoved(attachment)
        ? `${attachment.displayName} was removed or deleted`
        : attachment.displayName;
    }

    function toMessageAttachment(file: RecordFile): MessageAttachment {
      return {
        fileId: file.id,
        displayName: file.displayName,
        originalName: file.originalName,
        mimeType: file.mimeType,
        extension: file.extension,
        sizeBytes: file.sizeBytes,
        storagePath: file.storagePath,
        version: file.version,
        previewKind: getRecordFilePreviewKind(file)
      };
    }

    async function ensureAttachmentPreviewUrl(attachment: MessageAttachment): Promise<void> {
      const signature = buildAttachmentSignature(attachment);
      if (attachmentPreviewSignatures.value[attachment.fileId] === signature && attachmentPreviewUrls.value[attachment.fileId]) {
        return;
      }

      if (pendingAttachmentPreviewIds.has(attachment.fileId) || getAttachmentPreviewKind(attachment) !== 'image') {
        return;
      }

      pendingAttachmentPreviewIds.add(attachment.fileId);

      try {
        const nextUrl = await fileClient.getDownloadUrl(attachment.storagePath);
        attachmentPreviewUrls.value = {
          ...attachmentPreviewUrls.value,
          [attachment.fileId]: nextUrl
        };
        attachmentPreviewSignatures.value = {
          ...attachmentPreviewSignatures.value,
          [attachment.fileId]: signature
        };
      } catch (error) {
        console.warn('[ShipmentActivityPane] Failed to load attachment preview:', error);
      } finally {
        pendingAttachmentPreviewIds.delete(attachment.fileId);
      }
    }

    function syncAttachmentPreviews(nextAttachments: MessageAttachment[]): void {
      for (const attachment of nextAttachments) {
        if (getAttachmentPreviewKind(attachment) === 'image') {
          void ensureAttachmentPreviewUrl(attachment);
        }
      }
    }

    function clearAttachmentInput(): void {
      if (attachmentInputRef.value) {
        attachmentInputRef.value.value = '';
      }
    }

    function triggerAttachmentInput(): void {
      if (!canAttachFiles.value) {
        return;
      }

      attachmentInputRef.value?.click();
    }

    function closeComposerMenu(): void {
      composerMenuRenderKey.value += 1;
    }

    function handleComposerMenuUpload(): void {
      triggerAttachmentInput();
    }

    function handleComposerMenuSelect(actionKey: string): void {
      if (actionKey === 'upload') {
        handleComposerMenuUpload();
      }
    }

    async function insertComposerTokenAndBrowse(
      kind: ForcedComposerSuggestionKind,
      token: string
    ): Promise<void> {
      expandComposer();
      const textarea = getComposerElement();
      if (!textarea || !composerRef.value || isSending.value) {
        return;
      }

      if (kind === 'mention') {
        await ensureUserStoreReady();
      }

      const caretPosition = textarea.selectionStart ?? draft.value.length;
      const existingContext = extractComposerTokenContext(draft.value, caretPosition);
      if (existingContext?.kind === kind) {
        forcedComposerSuggestionKind.value = kind;
        dismissedComposerTokenKey.value = '';
        void updateComposerSuggestions();
        composerRef.value.focus();
        return;
      }

      const beforeCaret = draft.value.slice(0, caretPosition);
      const afterCaret = draft.value.slice(caretPosition);
      const needsLeadingSpace = beforeCaret.length > 0 && !/\s$/.test(beforeCaret);
      const insertion = `${needsLeadingSpace ? ' ' : ''}${token}`;
      const nextDraft = `${beforeCaret}${insertion}${afterCaret}`;
      const nextCaretPosition = caretPosition + insertion.length;

      draft.value = nextDraft;
      forcedComposerSuggestionKind.value = kind;
      dismissedComposerTokenKey.value = '';

      await nextTick();
      composerRef.value.focus();
      composerRef.value.setSelectionRange(nextCaretPosition, nextCaretPosition);
      handleComposerInput();
      void updateComposerSuggestions();
    }

    async function handleComposerMentionAction(): Promise<void> {
      await insertComposerTokenAndBrowse('mention', '@');
    }

    async function handleComposerShipmentLinkAction(): Promise<void> {
      await insertComposerTokenAndBrowse('shipment', '#');
    }

    async function handleComposerPaperclipAction(): Promise<void> {
      if (!canAttachFiles.value) {
        return;
      }

      await insertComposerTokenAndBrowse('file', '/file');
    }

    function releaseUploadingDraftAttachments(attachments: UploadingDraftAttachment[]): void {
      for (const attachment of attachments) {
        if (attachment.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      }
    }

    function setUploadingDraftAttachments(nextAttachments: UploadingDraftAttachment[]): void {
      releaseUploadingDraftAttachments(uploadingDraftAttachments.value);
      uploadingDraftAttachments.value = nextAttachments;
    }

    function buildUploadingDraftAttachments(files: File[]): UploadingDraftAttachment[] {
      return files.map((file, index) => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        const previewKind = getRecordFilePreviewKind({
          displayName: file.name,
          originalName: file.name,
          mimeType: file.type || 'application/octet-stream',
          extension
        });

        return {
          key: `upload-${Date.now()}-${index}-${file.name}`,
          displayName: file.name,
          previewKind,
          previewLabel: getRecordFilePreviewLabel({
            displayName: file.name,
            originalName: file.name,
            mimeType: file.type || 'application/octet-stream',
            extension
          }),
          previewUrl: previewKind === 'image' ? URL.createObjectURL(file) : null,
          progressPercent: 0
        };
      });
    }

    function updateUploadingDraftAttachmentProgress(uploadIndex: number, filePercent: number): void {
      uploadingDraftAttachments.value = uploadingDraftAttachments.value.map((attachment, index) => ({
        ...attachment,
        progressPercent: index < uploadIndex ? 100 : index === uploadIndex ? filePercent : attachment.progressPercent
      }));
    }

    function removeDraftAttachment(fileId: string): void {
      draftAttachments.value = draftAttachments.value.filter(attachment => attachment.fileId !== fileId);
      if (draftAttachments.value.length < 3 && noticeMessage.value.includes('up to 3 files')) {
        noticeMessage.value = '';
      }
    }

    async function openMessageAttachment(attachment: MessageAttachment): Promise<void> {
      errorMessage.value = '';

      if (isMessageAttachmentRemoved(attachment)) {
        return;
      }

      try {
        const url = await fileClient.getDownloadUrl(attachment.storagePath);
        window.open(url, '_blank', 'noopener');
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to open attachment';
      }
    }

    async function uploadComposerAttachments(fileList: FileList | File[]): Promise<void> {
      if (!tracking.value || isUploadingAttachments.value || isEditing.value) {
        return;
      }

      const nextFiles = Array.from(fileList);
      if (draftAttachments.value.length + nextFiles.length > 3) {
        noticeMessage.value = 'Messages can include up to 3 files. Add additional files on the Files tab.';
        clearAttachmentInput();
        return;
      }

      for (const file of nextFiles) {
        const validationError = validateLocalRecordFile(file);
        if (validationError) {
          errorMessage.value = validationError;
          clearAttachmentInput();
          return;
        }
      }

      const duplicateFileName = findDuplicateRecordFileUpload(nextFiles, draftAttachments.value);
      if (duplicateFileName) {
        noticeMessage.value = `${duplicateFileName} is already attached to this draft.`;
        clearAttachmentInput();
        return;
      }

      isUploadingAttachments.value = true;
      isDragOver.value = false;
      errorMessage.value = '';
      setUploadingDraftAttachments(buildUploadingDraftAttachments(nextFiles));

      try {
        const uploadedFiles = await uploadRecordFiles({
          recordType: 'shipments',
          recordId: tracking.value,
          files: nextFiles,
          onProgress: progress => {
            updateUploadingDraftAttachmentProgress(progress.uploadIndex, progress.filePercent);
          }
        });

        draftAttachments.value = [...draftAttachments.value, ...uploadedFiles.map(toMessageAttachment)];
        syncReadyFiles(uploadedFiles);
        syncAttachmentPreviews(draftAttachments.value);
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to upload attachment';
      } finally {
        isUploadingAttachments.value = false;
        setUploadingDraftAttachments([]);
        clearAttachmentInput();
      }
    }

    function handleAttachmentInputChange(event: Event): void {
      const input = event.target as HTMLInputElement | null;
      if (input?.files?.length) {
        void uploadComposerAttachments(input.files);
      }
    }

    function buildAttachmentCandidates(): MessageAttachmentCandidate[] | undefined {
      if (draftAttachments.value.length === 0) {
        return isEditing.value ? [] : undefined;
      }

      return draftAttachments.value.map(attachment => ({ fileId: attachment.fileId }));
    }

    function formatAttachmentSuggestionSecondary(file: RecordFile): string {
      const sizeLabel = file.sizeBytes < 1024
        ? `${file.sizeBytes} B`
        : file.sizeBytes < 1024 * 1024
          ? `${(file.sizeBytes / 1024).toFixed(1)} KB`
          : `${(file.sizeBytes / (1024 * 1024)).toFixed(1)} MB`;

      return [file.extension.toUpperCase(), sizeLabel].filter(Boolean).join(' • ');
    }

    function syncReadyFiles(nextFiles: RecordFile[]): void {
      const nextMap = new Map<string, RecordFile>();

      for (const file of readyFiles.value) {
        nextMap.set(file.id, file);
      }

      for (const file of nextFiles) {
        if (file.deleted === true || file.status !== 'ready') {
          nextMap.delete(file.id);
          continue;
        }

        nextMap.set(file.id, file);
      }

      readyFiles.value = Array.from(nextMap.values()).sort((left, right) => right.updatedAt - left.updatedAt);
    }

    async function loadReadyFiles(): Promise<RecordFile[]> {
      if (!tracking.value) {
        readyFiles.value = [];
        return [];
      }

      const snapshot = await getDocs(query(
        collection(db, 'shipments', tracking.value, 'files'),
        orderBy('updatedAt', 'desc'),
        limit(50)
      ));

      const nextFiles = snapshot.docs
        .map(docSnapshot => normalizeSnapshotFile(docSnapshot.id, docSnapshot.data()))
        .filter(file => file.deleted !== true && file.status === 'ready');

      readyFiles.value = nextFiles;
      return nextFiles;
    }

    async function loadFileSuggestions(
      queryText: string,
      options: { showFullList?: boolean } = {}
    ): Promise<Array<FileComposerSuggestion | UploadFileComposerSuggestion>> {
      const normalizedQuery = queryText.trim().toLowerCase();
      const resultLimit = options.showFullList ? 50 : 6;
      const availableFiles = readyFiles.value.length > 0 ? readyFiles.value : await loadReadyFiles();
      const attachedIds = new Set([
        ...draftAttachments.value.map(attachment => attachment.fileId),
        ...messages.value.flatMap(message => (message.attachments || []).map(attachment => attachment.fileId))
      ]);

      const fileSuggestions = availableFiles
        .filter(file => !attachedIds.has(file.id))
        .filter(file => {
          if (!normalizedQuery) {
            return true;
          }

          const haystack = [file.displayName, file.originalName, file.extension, file.mimeType]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return haystack.includes(normalizedQuery);
        })
        .slice(0, resultLimit)
        .map((file): FileComposerSuggestion => ({
          kind: 'file',
          key: `file-${file.id}`,
          fileId: file.id,
          label: file.displayName,
          secondary: formatAttachmentSuggestionSecondary(file),
          previewLabel: getRecordFilePreviewLabel(file),
          attachment: toMessageAttachment(file)
        }));

      return [
        ...fileSuggestions,
        {
          kind: 'file-upload',
          key: 'file-upload-action',
          label: 'Upload file',
          secondary: 'Add a new file from your computer'
        }
      ];
    }

    function attachExistingFile(attachment: MessageAttachment): boolean {
      if (draftAttachments.value.some(existing => existing.fileId === attachment.fileId)) {
        noticeMessage.value = `${attachment.displayName} is already attached to this draft.`;
        return false;
      }

      if (draftAttachments.value.length >= 3) {
        noticeMessage.value = 'Messages can include up to 3 files. Add additional files on the Files tab.';
        return false;
      }

      draftAttachments.value = [...draftAttachments.value, attachment];
      syncAttachmentPreviews(draftAttachments.value);
      noticeMessage.value = '';
      return true;
    }

    function handleDragEnter(): void {
      if (!canAttachFiles.value) {
        return;
      }

      expandComposer();
      errorMessage.value = '';
      isDragOver.value = true;
    }

    function handleDragLeave(event: DragEvent): void {
      if ((event.currentTarget as HTMLElement | null)?.contains(event.relatedTarget as Node | null)) {
        return;
      }

      isDragOver.value = false;
    }

    function handleDrop(event: DragEvent): void {
      isDragOver.value = false;
      if (event.dataTransfer?.files?.length) {
        void uploadComposerAttachments(event.dataTransfer.files);
      }
    }

    function clearEditingState(): void {
      editingMessageId.value = '';
      draft.value = '';
      draftAttachments.value = [];
      setUploadingDraftAttachments([]);
      selectedEntities.value = { mentions: [], shipments: [] };
      composerSuggestions.value = [];
      highlightedSuggestionIndex.value = 0;
      forcedComposerSuggestionKind.value = null;
      noticeMessage.value = '';
      closeComposerMenu();
      clearAttachmentInput();
      collapseComposer();
      syncComposerLayoutNextTick();
    }

    function restoreComposerDraftState(
      nextDraft: string,
      nextAttachments: MessageAttachment[],
      nextEntities: MessageEntityCandidates,
      nextNoticeMessage = ''
    ): void {
      draft.value = nextDraft;
      draftAttachments.value = [...nextAttachments];
      selectedEntities.value = {
        mentions: [...(nextEntities.mentions || [])],
        shipments: [...(nextEntities.shipments || [])]
      };
      noticeMessage.value = nextNoticeMessage;
      syncAttachmentPreviews(draftAttachments.value);
      expandComposer();
      syncComposerLayoutNextTick();
    }

    function getMentionSecondaryText(uid: string, fallbackDisplayName: string): string {
      const matchingProfile = UserStore.getAllProfiles().find(profile => profile.uid === uid);
      return matchingProfile?.slack?.real_name
        || matchingProfile?.displayName
        || fallbackDisplayName.replace(/^@/, '');
    }

    function getRenderableComposerEntities(messageText: string): Array<{ kind: 'mention' | 'shipment'; start: number; end: number; }> {
      const entities: Array<{ kind: 'mention' | 'shipment'; start: number; end: number; }> = [];
      const usedRanges: Array<{ start: number; end: number; }> = [];

      const tryAddEntity = (kind: 'mention' | 'shipment', label: string): void => {
        if (!label) {
          return;
        }

        const start = messageText.indexOf(label);
        const end = start + label.length;
        if (start < 0 || usedRanges.some(range => start < range.end && end > range.start)) {
          return;
        }

        usedRanges.push({ start, end });
        entities.push({ kind, start, end });
      };

      (selectedEntities.value.mentions || []).forEach(mention => tryAddEntity('mention', mention.label || ''));
      (selectedEntities.value.shipments || []).forEach(shipment => tryAddEntity('shipment', shipment.label || ''));

      return entities.sort((left, right) => left.start - right.start);
    }

    async function focusComposer(): Promise<void> {
      expandComposer();
      await nextTick();

      const textarea = getComposerElement();
      if (!textarea || !composerRef.value) {
        return;
      }

      composerRef.value.focus();
      const end = textarea.value.length;
      composerRef.value.setSelectionRange(end, end);
      handleComposerInput();
    }

    function startEditingMessage(message: MessageRecord): void {
      if (!canEditMessage(message) || isSending.value || deletingMessageId.value === message.id) {
        return;
      }

      closeMessageMenu();
      editingMessageId.value = message.id;
      draft.value = message.text;
      draftAttachments.value = [...(message.attachments || [])];
      syncAttachmentPreviews(draftAttachments.value);
      selectedEntities.value = {
        mentions: (message.entities?.mentions || []).map((mention): MessageMentionCandidate => ({
          uid: mention.uid,
          label: mention.label,
          start: mention.start ?? undefined,
          end: mention.end ?? undefined
        })),
        shipments: (message.entities?.shipments || []).map((shipment): MessageShipmentReferenceCandidate => ({
          tracking: shipment.trackingNumber,
          canonicalTracking: shipment.canonicalTracking,
          label: shipment.label,
          start: shipment.start ?? undefined,
          end: shipment.end ?? undefined
        }))
      };
      errorMessage.value = '';
      void focusComposer();
    }

    function cancelEditingMessage(): void {
      clearEditingState();
      errorMessage.value = '';
    }

    function closeMessageMenu(): void {
      messageMenuRenderKey.value += 1;
    }

    function handleDocumentPointerDown(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;

      if (target?.closest('.ui-overflow-menu')
        || target?.closest('.ui-overflow-menu__panel')
        || target?.closest('.composer-suggestion-list')
        || target?.closest('.message-composer')) {
        return;
      }

      closeComposerMenu();
      composerSuggestions.value = [];
      collapseComposer();
    }

    function getMessageMenuOptions(message: MessageRecord): OverflowMenuOption[] {
      return [
        {
          key: 'edit',
          label: 'Edit',
          icon: 'edit',
          disabled: !canEditMessage(message) || isSending.value
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: 'delete',
          tone: 'danger',
          disabled: !canDeleteMessage(message) || deletingMessageId.value === message.id
        }
      ];
    }

    function handleMessageMenuSelect(actionKey: string, message: MessageRecord): void {
      if (actionKey === 'edit') {
        handleMenuEdit(message);
        return;
      }

      if (actionKey === 'delete') {
        handleMenuDelete(message);
      }
    }

    function handleMenuEdit(message: MessageRecord): void {
      if (!canEditMessage(message) || isSending.value) {
        return;
      }

      closeMessageMenu();
      startEditingMessage(message);
    }

    function handleMenuDelete(message: MessageRecord): void {
      if (!canDeleteMessage(message) || deletingMessageId.value === message.id) {
        return;
      }

      closeMessageMenu();
      void confirmDeleteMessage(message);
    }

    async function confirmDeleteMessage(message: MessageRecord): Promise<void> {
      if (!canDeleteMessage(message) || deletingMessageId.value) {
        return;
      }

      const shouldDelete = window.confirm(
        message.attachments && message.attachments.length > 0
          ? 'Delete this message? Attached files will remain available in the Files tab.'
          : 'Delete this message?'
      );
      if (!shouldDelete) {
        return;
      }

      deletingMessageId.value = message.id;
      errorMessage.value = '';
      messageActionError.value = '';

      try {
        await messageClient.deleteMessage('shipments', tracking.value, message.id);
        messages.value = messages.value.filter(existingMessage => existingMessage.id !== message.id);

        if (editingMessageId.value === message.id) {
          clearEditingState();
        }
      } catch (error) {
        const messageText = error instanceof Error ? error.message : 'Failed to delete message';
        messageActionError.value = messageText;
      } finally {
        deletingMessageId.value = '';
      }
    }

    function resetComposerHeight(): void {
      syncComposerLayoutNextTick();
    }

    function handleComposerInput(): void {
      syncComposerLayoutNextTick();
      pruneSelectedEntities();
      void updateComposerSuggestions();
    }

    function setComposerSuggestions(nextSuggestions: ComposerSuggestion[]): void {
      const highlightedKey = composerSuggestions.value[highlightedSuggestionIndex.value]?.key || '';

      composerSuggestions.value = nextSuggestions;
      if (nextSuggestions.length === 0) {
        highlightedSuggestionIndex.value = 0;
        return;
      }

      const preservedIndex = highlightedKey
        ? nextSuggestions.findIndex(suggestion => suggestion.key === highlightedKey)
        : -1;

      highlightedSuggestionIndex.value = preservedIndex >= 0 ? preservedIndex : 0;
    }

    function handleComposerSelectionChange(event?: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent && ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab', 'Escape'].includes(event.key)) {
        return;
      }

      void updateComposerSuggestions();
    }

    function handleComposerKeydown(event: KeyboardEvent): void {
      const caretPosition = getComposerElement()?.selectionStart ?? draft.value.length;
      const tokenContext = extractComposerTokenContext(draft.value, caretPosition);

      if (event.key === 'Escape' && tokenContext) {
        event.preventDefault();
        event.stopPropagation();
        composerSuggestions.value = [];
        highlightedSuggestionIndex.value = 0;
        dismissedComposerTokenKey.value = getComposerTokenKey(tokenContext);
        forcedComposerSuggestionKind.value = null;
        return;
      }

      if (composerSuggestions.value.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value + 1) % composerSuggestions.value.length;
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          highlightedSuggestionIndex.value = (highlightedSuggestionIndex.value - 1 + composerSuggestions.value.length) % composerSuggestions.value.length;
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          composerSuggestions.value = [];
          forcedComposerSuggestionKind.value = null;
          return;
        }

        if ((event.key === 'Enter' || event.key === 'Tab') && composerSuggestions.value[highlightedSuggestionIndex.value]) {
          event.preventDefault();
          applyComposerSuggestion(highlightedSuggestionIndex.value);
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void submitMessage();
      }
    }

    function extractComposerTokenContext(text: string, caretPosition: number): ComposerTokenContext | null {
      const beforeCaret = text.slice(0, caretPosition);
      const tokenMatch = beforeCaret.match(/(^|[\s\n])(\/file(?:\s+[^\n]*)?|[@#][^\s@#]*)$/);
      if (!tokenMatch) {
        return null;
      }

      const token = tokenMatch[2];
      if (token.startsWith('/file')) {
        return {
          kind: 'file',
          query: token.slice('/file'.length).trim(),
          start: beforeCaret.length - token.length,
          end: beforeCaret.length
        };
      }

      const kind = token.startsWith('@') ? 'mention' : 'shipment';
      return {
        kind,
        query: token.slice(1),
        start: beforeCaret.length - token.length,
        end: beforeCaret.length
      };
    }

    function pruneSelectedEntities(): void {
      const nextDraft = draft.value;
      selectedEntities.value = {
        mentions: (selectedEntities.value.mentions || []).filter(mention => mention.label && nextDraft.includes(mention.label)),
        shipments: (selectedEntities.value.shipments || []).filter(shipment => shipment.label && nextDraft.includes(shipment.label))
      };
    }

    function getComposerTokenKey(tokenContext: ComposerTokenContext): string {
      const normalizedQuery = tokenContext.kind === 'shipment'
        ? canonicalizeTrackingValue(tokenContext.query)
        : tokenContext.query.trim().toLowerCase();

      return `${tokenContext.kind}:${tokenContext.start}:${normalizedQuery}`;
    }

    function buildMentionSuggestions(mentionQuery: string, showFullList: boolean): MentionComposerSuggestion[] {
      const users = showFullList && !mentionQuery
        ? Object.values(UserStore.getAllUsers()).filter(user => Boolean(user.uid))
        : UserStore.searchUsers(mentionQuery);

      const resultLimit = showFullList ? 100 : 6;

      return users
        .slice()
        .sort((left, right) => left.display_name.localeCompare(right.display_name))
        .slice(0, resultLimit)
        .map((user): MentionComposerSuggestion => ({
          kind: 'mention',
          key: `mention-${user.uid}`,
          uid: user.uid as string,
          label: getMentionInsertionLabel(user.display_name),
          secondary: getMentionSecondaryText(user.uid as string, user.display_name),
          avatar: user.avatar
        }));
    }

    async function updateComposerSuggestions(): Promise<void> {
      const textarea = getComposerElement();
      if (!textarea) {
        composerSuggestions.value = [];
        forcedComposerSuggestionKind.value = null;
        return;
      }

      const caretPosition = textarea.selectionStart ?? draft.value.length;
      const tokenContext = extractComposerTokenContext(draft.value, caretPosition);
      if (!tokenContext) {
        composerSuggestions.value = [];
        dismissedComposerTokenKey.value = '';
        forcedComposerSuggestionKind.value = null;
        return;
      }

      if (forcedComposerSuggestionKind.value && forcedComposerSuggestionKind.value !== tokenContext.kind) {
        forcedComposerSuggestionKind.value = null;
      }

      const tokenKey = getComposerTokenKey(tokenContext);
      if (dismissedComposerTokenKey.value === tokenKey) {
        composerSuggestions.value = [];
        return;
      }

      if (tokenContext.kind === 'mention') {
        void userStoreVersion.value;
        const mentionQuery = tokenContext.query.trim();
        const showFullList = forcedComposerSuggestionKind.value === 'mention';

        if (mentionQuery.length < 2 && !showFullList) {
          composerSuggestions.value = [];
          return;
        }

        setComposerSuggestions(buildMentionSuggestions(mentionQuery, showFullList));
        return;
      }

      if (tokenContext.kind === 'file') {
        const requestId = ++activeSuggestionRequestId;
        const showFullList = forcedComposerSuggestionKind.value === 'file';
        const nextSuggestions = await loadFileSuggestions(tokenContext.query, { showFullList });
        if (requestId !== activeSuggestionRequestId) {
          return;
        }

        setComposerSuggestions(nextSuggestions);
        return;
      }

      const shipmentQuery = tokenContext.query.trim();
      const canonicalQuery = canonicalizeTrackingValue(shipmentQuery);
      const showFullList = forcedComposerSuggestionKind.value === 'shipment';

      if (canonicalQuery.length < 4 && !showFullList) {
        composerSuggestions.value = [];
        return;
      }

      const requestId = ++activeSuggestionRequestId;
      const nextSuggestions = await loadShipmentSuggestions(shipmentQuery, canonicalQuery, {
        showFullList,
        resultLimit: showFullList ? 50 : 5
      });
      if (requestId !== activeSuggestionRequestId) {
        return;
      }

      setComposerSuggestions(nextSuggestions);
    }

    async function loadShipmentSuggestions(
      rawQuery: string,
      canonicalQuery: string,
      options: ShipmentSuggestionLoadOptions = {}
    ): Promise<ShipmentComposerSuggestion[]> {
      const { showFullList = false, resultLimit = 5 } = options;
      const cacheKey = showFullList && !canonicalQuery ? '__active_all__' : canonicalQuery;

      if (shipmentSuggestionCache.has(cacheKey)) {
        return shipmentSuggestionCache.get(cacheKey) || [];
      }

      if (showFullList && canonicalQuery.length > 0 && canonicalQuery.length < 4) {
        const allSuggestions = shipmentSuggestionCache.get('__active_all__')
          ?? await loadShipmentSuggestions('', '', { showFullList: true, resultLimit: Math.max(resultLimit, 50) });
        const loweredQuery = rawQuery.toLowerCase();

        return allSuggestions
          .filter(suggestion => suggestion.canonicalTracking.includes(canonicalQuery)
            || suggestion.tracking.toLowerCase().includes(loweredQuery)
            || suggestion.label.toLowerCase().includes(loweredQuery))
          .slice(0, resultLimit);
      }

      const suggestions = new Map<string, ShipmentComposerSuggestion>();
      const currentTracking = canonicalizeTrackingValue(tracking.value);

      const addActiveSuggestion = (shipment: {
        tracking: string;
        canonicalTracking: string;
        label: string;
        secondary?: string;
      }): void => {
        if (!shipment?.canonicalTracking || suggestions.has(shipment.canonicalTracking)) {
          return;
        }

        suggestions.set(shipment.canonicalTracking, {
          kind: 'shipment',
          key: `shipment-${shipment.canonicalTracking}`,
          tracking: shipment.tracking,
          canonicalTracking: shipment.canonicalTracking,
          label: shipment.label,
          secondary: shipment.secondary || ''
        });
      };

      if (window.tracker?.getActiveShipmentSuggestions) {
        const activeSuggestions = window.tracker.getActiveShipmentSuggestions(
          rawQuery,
          tracking.value,
          showFullList && !canonicalQuery ? Math.max(resultLimit, 50) : resultLimit
        ) || [];
        activeSuggestions.forEach(addActiveSuggestion);
      }

      if (showFullList && !canonicalQuery) {
        const nextSuggestions = Array.from(suggestions.values())
          .sort((left, right) => left.label.localeCompare(right.label))
          .slice(0, resultLimit);
        shipmentSuggestionCache.set(cacheKey, nextSuggestions);
        return nextSuggestions;
      }

      if (suggestions.size > 0) {
        const nextSuggestions = Array.from(suggestions.values()).slice(0, resultLimit);
        shipmentSuggestionCache.set(cacheKey, nextSuggestions);
        return nextSuggestions;
      }

      const addSuggestion = (shipmentData: any, fallbackTracking: string): void => {
        const trackingNumber = shipmentData?.tracking?.tracking_no || shipmentData?.tracking_no || fallbackTracking;
        const canonicalTracking = canonicalizeTrackingValue(trackingNumber);
        if (!canonicalTracking || canonicalTracking === currentTracking || suggestions.has(canonicalTracking) || shipmentData?.stage !== 'active') {
          return;
        }

        const displayTracking = shipmentData?.tracking?.formatted_tracking_no || shipmentData?.tracking?.tracking_no || shipmentData?.tracking_no || fallbackTracking;
        const destinationCity = shipmentData?.waypointIndex?.destination?.city || shipmentData?.destination?.city || '';
        const destinationCountry = shipmentData?.waypointIndex?.destination?.countrycode || shipmentData?.destination?.countrycode || shipmentData?.destination?.country || '';
        const status = shipmentData?.status || shipmentData?.stage || '';
        const secondary = [destinationCity && destinationCountry ? `${destinationCity}, ${destinationCountry}` : destinationCity || destinationCountry, status]
          .filter(Boolean)
          .join(' • ');

        suggestions.set(canonicalTracking, {
          kind: 'shipment',
          key: `shipment-${canonicalTracking}`,
          tracking: trackingNumber,
          canonicalTracking,
          label: getShipmentInsertionLabel(displayTracking),
          secondary
        });
      };

      const docSnapshot = await getDoc(doc(db, 'shipments', canonicalQuery));
      if (docSnapshot.exists()) {
        addSuggestion(docSnapshot.data(), canonicalQuery);
      }

      const rawCandidates = [...new Set([rawQuery.trim(), rawQuery.trim().toUpperCase(), canonicalQuery].filter(Boolean))];
      for (const candidate of rawCandidates) {
        const [masterSnapshot, legacySnapshot] = await Promise.all([
          getDocs(query(collection(db, 'shipments'), where('tracking.tracking_no', '==', candidate), limit(1))),
          getDocs(query(collection(db, 'shipments'), where('tracking_no', '==', candidate), limit(1)))
        ]);

        masterSnapshot.docs.forEach(docSnapshot => addSuggestion(docSnapshot.data(), docSnapshot.id));
        legacySnapshot.docs.forEach(docSnapshot => addSuggestion(docSnapshot.data(), docSnapshot.id));

        if (suggestions.size > 0) {
          break;
        }
      }

      const nextSuggestions = Array.from(suggestions.values()).slice(0, resultLimit);
      shipmentSuggestionCache.set(cacheKey, nextSuggestions);
      return nextSuggestions;
    }

    function applyComposerSuggestion(index: number): void {
      const textarea = getComposerElement();
      const suggestion = composerSuggestions.value[index];
      if (!textarea || !suggestion) {
        return;
      }

      const caretPosition = textarea.selectionStart ?? draft.value.length;
      const tokenContext = extractComposerTokenContext(draft.value, caretPosition);
      if (!tokenContext) {
        composerSuggestions.value = [];
        forcedComposerSuggestionKind.value = null;
        return;
      }

      const insertionLabel = suggestion.label;
      const prefix = draft.value.slice(0, tokenContext.start);
      const suffix = draft.value.slice(tokenContext.end);

      if (suggestion.kind === 'file-upload') {
        const nextDraft = `${prefix}${suffix}`;
        const nextCaretPosition = prefix.length;

        draft.value = nextDraft;
        composerSuggestions.value = [];
        highlightedSuggestionIndex.value = 0;
        dismissedComposerTokenKey.value = '';
        forcedComposerSuggestionKind.value = null;

        void nextTick(() => {
          if (!composerRef.value) {
            return;
          }

          composerRef.value.focus();
          composerRef.value.setSelectionRange(nextCaretPosition, nextCaretPosition);
          handleComposerInput();
          triggerAttachmentInput();
        });
        return;
      }

      if (suggestion.kind === 'file') {
        if (!attachExistingFile(suggestion.attachment)) {
          return;
        }

        const nextDraft = `${prefix}${suffix}`;
        const nextCaretPosition = prefix.length;

        draft.value = nextDraft;
        composerSuggestions.value = [];
        highlightedSuggestionIndex.value = 0;
        dismissedComposerTokenKey.value = '';
        forcedComposerSuggestionKind.value = null;
        closeComposerMenu();

        void nextTick(() => {
          if (!composerRef.value) {
            return;
          }

          composerRef.value.focus();
          composerRef.value.setSelectionRange(nextCaretPosition, nextCaretPosition);
          handleComposerInput();
        });
        return;
      }

      const spacer = /^\s/.test(suffix) ? '' : ' ';
      const nextDraft = `${prefix}${insertionLabel}${spacer}${suffix}`;
      const nextCaretPosition = prefix.length + insertionLabel.length + spacer.length;

      draft.value = nextDraft;
      if (suggestion.kind === 'mention') {
        selectedEntities.value = {
          ...selectedEntities.value,
          mentions: upsertMentionCandidate(selectedEntities.value.mentions || [], suggestion)
        };
      } else {
        selectedEntities.value = {
          ...selectedEntities.value,
          shipments: upsertShipmentCandidate(selectedEntities.value.shipments || [], suggestion)
        };
      }

      composerSuggestions.value = [];
      highlightedSuggestionIndex.value = 0;
      dismissedComposerTokenKey.value = '';
      forcedComposerSuggestionKind.value = null;

      void nextTick(() => {
        if (!composerRef.value) {
          return;
        }

        composerRef.value.focus();
        composerRef.value.setSelectionRange(nextCaretPosition, nextCaretPosition);
        handleComposerInput();
      });
    }

    function upsertMentionCandidate(
      mentions: MessageMentionCandidate[],
      suggestion: MentionComposerSuggestion
    ): MessageMentionCandidate[] {
      const nextMentions = mentions.filter(mention => mention.uid !== suggestion.uid);
      nextMentions.push({
        uid: suggestion.uid,
        label: suggestion.label
      });
      return nextMentions;
    }

    function upsertShipmentCandidate(
      shipments: MessageShipmentReferenceCandidate[],
      suggestion: ShipmentComposerSuggestion
    ): MessageShipmentReferenceCandidate[] {
      const nextShipments = shipments.filter(shipment => shipment.canonicalTracking !== suggestion.canonicalTracking);
      nextShipments.push({
        tracking: suggestion.tracking,
        canonicalTracking: suggestion.canonicalTracking,
        label: suggestion.label
      });
      return nextShipments;
    }

    function buildImplicitMentionCandidates(
      messageText: string,
      existingMentionUids: Set<string>
    ): MessageMentionCandidate[] {
      const profiles = UserStore.getAllProfiles();
      if (profiles.length === 0) {
        return [];
      }

      const lookup = new Map<string, string>();

      profiles.forEach(profile => {
        const aliases = [
          profile.slack?.display_name,
          profile.slack?.real_name,
          profile.displayName,
          profile.email,
          profile.email?.split('@')[0]
        ];

        aliases
          .filter((alias): alias is string => typeof alias === 'string' && alias.trim().length > 0)
          .forEach(alias => {
            lookup.set(alias.trim().toLowerCase(), profile.uid);
          });
      });

      const mentions: MessageMentionCandidate[] = [];
      const mentionPattern = /(^|[\s\n])@([^\s@#]+)/g;
      let match: RegExpExecArray | null;

      while ((match = mentionPattern.exec(messageText)) !== null) {
        const mentionToken = match[2]?.trim().toLowerCase();
        const uid = mentionToken ? lookup.get(mentionToken) : undefined;
        if (!uid || existingMentionUids.has(uid)) {
          continue;
        }

        const start = match.index + match[1].length;
        const label = messageText.slice(start, start + match[0].trim().length);

        mentions.push({
          uid,
          label,
          start,
          end: start + label.length
        });
        existingMentionUids.add(uid);
      }

      return mentions;
    }

    function buildEntityCandidatesFromDraft(messageText: string): MessageEntityCandidates | undefined {
      const mentions = (selectedEntities.value.mentions || [])
        .map((mention): MessageMentionCandidate | null => {
          const label = mention.label || '';
          const start = messageText.indexOf(label);
          if (!label || start < 0) {
            return null;
          }

          return {
            uid: mention.uid,
            label,
            start,
            end: start + label.length
          };
        })
        .filter((mention): mention is MessageMentionCandidate => Boolean(mention));

      const mentionUids = new Set(mentions.map(mention => mention.uid));
      mentions.push(...buildImplicitMentionCandidates(messageText, mentionUids));

      const shipments = (selectedEntities.value.shipments || [])
        .map((shipment): MessageShipmentReferenceCandidate | null => {
          const label = shipment.label || '';
          const start = messageText.indexOf(label);
          if (!label || start < 0) {
            return null;
          }

          return {
            tracking: shipment.tracking,
            canonicalTracking: shipment.canonicalTracking,
            label,
            start,
            end: start + label.length
          };
        })
        .filter((shipment): shipment is MessageShipmentReferenceCandidate => Boolean(shipment));

      if (mentions.length === 0 && shipments.length === 0) {
        return undefined;
      }

      return { mentions, shipments };
    }

    function applyMessageWarnings(warnings: MessageRecord['warnings']): void {
      const nextWarnings = warnings || [];
      if (nextWarnings.length === 0) {
        noticeMessage.value = '';
        return;
      }

      noticeMessage.value = nextWarnings.map(warning => warning.message).join(' ');
    }

    function upsertMessageRecord(nextMessage: MessageRecord): void {
      const nextTimestampMs = nextMessage.timestampMs > 0 ? nextMessage.timestampMs : Date.now();
      const normalizedMessage: MessageRecord = {
        ...nextMessage,
        timestampMs: nextTimestampMs,
        timestamp: Math.floor(nextTimestampMs / 1000)
      };

      const existingIndex = messages.value.findIndex(message => message.id === normalizedMessage.id);
      if (existingIndex < 0) {
        messages.value = [...messages.value, normalizedMessage];
        return;
      }

      messages.value = messages.value.map((message, index) => {
        if (index !== existingIndex) {
          return message;
        }

        return {
          ...message,
          ...normalizedMessage
        };
      });
    }

    async function handleTimelineClick(event: MouseEvent): Promise<void> {
      const target = event.target as HTMLElement | null;
      const shipmentLink = target?.closest('.timeline-entity-shipment') as HTMLElement | null;
      if (!shipmentLink || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }

      const linkedTracking = shipmentLink.getAttribute('data-tracking');
      event.preventDefault();
      if (!linkedTracking || !window.tracker?.openShipmentRecordByTracking) {
        noticeMessage.value = 'Unable to open the linked shipment. Please try again.';
        return;
      }

      try {
        await window.tracker.openShipmentRecordByTracking(linkedTracking, { showModal: false });
      } catch (error) {
        console.warn('[ShipmentActivityPane] Failed to open shipment reference:', error);
        noticeMessage.value = 'Unable to open the linked shipment. Please try again.';
      }
    }

    function handleTimelineScroll(): void {
      if (!timelineRef.value) {
        showJumpButton.value = false;
        return;
      }

      const distanceFromBottom = timelineRef.value.scrollHeight - timelineRef.value.scrollTop - timelineRef.value.clientHeight;
      showJumpButton.value = distanceFromBottom > 50;
    }

    function scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
      if (!timelineRef.value) {
        return;
      }

      timelineRef.value.scrollTo({
        top: timelineRef.value.scrollHeight,
        behavior
      });
      handleTimelineScroll();
    }

    function getLatestOtherUserMessageTimestamp(nextMessages: MessageRecord[]): number {
      const userId = currentUserId.value;
      if (!userId) {
        return 0;
      }

      const latestOtherUserMessage = nextMessages.find(message => (
        message.legacy !== true
        && message.deleted !== true
        && message.authorId !== userId
        && message.timestampMs > 0
      ));

      return latestOtherUserMessage?.timestampMs ?? 0;
    }

    function shouldMarkMessagesAsRead(nextMessages: MessageRecord[], requestedTracking: string): boolean {
      const userId = currentUserId.value;
      if (!userId) {
        return false;
      }

      const unreadBy = record.value?.messaging?.unreadBy;
      if (Array.isArray(unreadBy) && unreadBy.includes(userId)) {
        return true;
      }

      const latestOtherUserMessageTimestamp = getLatestOtherUserMessageTimestamp(nextMessages);
      if (latestOtherUserMessageTimestamp === 0) {
        return false;
      }

      return latestOtherUserMessageTimestamp > (readReceiptReconciliation.get(requestedTracking) || 0);
    }

    async function markMessagesAsReadIfNeeded(nextMessages: MessageRecord[], requestedTracking: string, requestId: number): Promise<void> {
      if (!shouldMarkMessagesAsRead(nextMessages, requestedTracking)) {
        return;
      }

      const latestOtherUserMessageTimestamp = getLatestOtherUserMessageTimestamp(nextMessages);

      try {
        await messageClient.markMessagesAsRead('shipments', requestedTracking);
        readReceiptReconciliation.set(requestedTracking, latestOtherUserMessageTimestamp);

        if (requestId !== activeRequestId || tracking.value !== requestedTracking || !record.value) {
          return;
        }

        const unreadBy = Array.isArray(record.value?.messaging?.unreadBy)
          ? record.value.messaging.unreadBy.filter((userId: string) => userId !== currentUserId.value)
          : [];

        record.value = {
          ...record.value,
          messaging: {
            ...(record.value.messaging || {}),
            unreadBy
          }
        };
      } catch (error) {
        console.warn('[ShipmentActivityPane] Failed to mark messages as read:', error);
      }
    }

    function handleLocationClick(coords: [number, number] | null): void {
      if (!coords || !window.tracker?.shipmentMap?.flyToLocation) {
        return;
      }

      window.tracker.shipmentMap.flyToLocation(coords, 4);
    }

    function handleWaypointEdit(waypointId: string | null): void {
      if (!waypointId || !window.tracker?.editWaypoint) {
        return;
      }

      window.tracker.editWaypoint(waypointId);
    }

    function stopLiveMessageSubscription(): void {
      activeRequestId += 1;
      if (liveMessagesUnsubscribe) {
        liveMessagesUnsubscribe();
        liveMessagesUnsubscribe = null;
      }
    }

    function startLiveMessageSubscription(requestedTracking: string): void {
      stopLiveMessageSubscription();
      messages.value = [];
      errorMessage.value = '';
      isLoading.value = Boolean(requestedTracking);
      if (!requestedTracking) return;

      const requestId = activeRequestId;
      const isCurrent = () => requestId === activeRequestId && tracking.value === requestedTracking;
      liveMessagesUnsubscribe = subscribeMessageFeed(
        'shipments', requestedTracking, {
          uid: currentUserId.value,
          isAdmin: currentUser.value?.permissions?.adminAccess === true
        },
        nextMessages => {
          if (!isCurrent()) return;
          messages.value = nextMessages;
          syncAttachmentPreviews(nextMessages.flatMap(message => message.attachments || []));
          isLoading.value = false;
          void markMessagesAsReadIfNeeded(nextMessages, requestedTracking, requestId);
          void nextTick().then(() => {
            if (isCurrent()) scrollToBottom();
          });
        },
        error => {
          if (!isCurrent()) return;
          console.warn('[ShipmentActivityPane] Live message feed failed:', error);
          noticeMessage.value = 'Messages are temporarily unavailable. Event activity is still shown.';
          isLoading.value = false;
        }
      );
    }

    async function submitMessage(): Promise<void> {
      if (isSendDisabled.value) {
        return;
      }

      const submittedText = draft.value.trim();
      const targetMessageId = editingMessageId.value;
      const entityCandidates = buildEntityCandidatesFromDraft(submittedText);
      const attachmentCandidates = buildAttachmentCandidates();
      const draftSnapshot = draft.value;
      const attachmentSnapshot = [...draftAttachments.value];
      const selectedEntitiesSnapshot: MessageEntityCandidates = {
        mentions: [...(selectedEntities.value.mentions || [])],
        shipments: [...(selectedEntities.value.shipments || [])]
      };
      const noticeSnapshot = noticeMessage.value;
      isSending.value = true;
      errorMessage.value = '';
      messageActionError.value = '';

      try {
        if (targetMessageId) {
          const updatedMessage = await messageClient.updateMessage(
            'shipments',
            tracking.value,
            targetMessageId,
            submittedText,
            entityCandidates,
            attachmentCandidates
          );

          messages.value = messages.value.map(existingMessage => {
            if (existingMessage.id !== targetMessageId) {
              return existingMessage;
            }

            const nextTimestampMs = updatedMessage.timestampMs > 0
              ? updatedMessage.timestampMs
              : existingMessage.timestampMs;

            return {
              ...existingMessage,
              ...updatedMessage,
              timestampMs: nextTimestampMs,
              timestamp: Math.floor(nextTimestampMs / 1000)
            };
          });
          syncAttachmentPreviews(updatedMessage.attachments || []);

          clearEditingState();
          applyMessageWarnings(updatedMessage.warnings);
          await nextTick();
          return;
        }

        clearEditingState();
        await nextTick();

        const createdMessage = await messageClient.createMessage(
          'shipments',
          tracking.value,
          submittedText,
          entityCandidates,
          attachmentCandidates
        );
        upsertMessageRecord(createdMessage);
        syncAttachmentPreviews(createdMessage.attachments || []);
        readReceiptReconciliation.set(tracking.value, getLatestOtherUserMessageTimestamp(messages.value));
        applyMessageWarnings(createdMessage.warnings);
        await nextTick();
        scrollToBottom('smooth');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send message';
        messageActionError.value = message;
        if (!targetMessageId) {
          restoreComposerDraftState(draftSnapshot, attachmentSnapshot, selectedEntitiesSnapshot, noticeSnapshot);
          await focusComposer();
        }
      } finally {
        isSending.value = false;
      }
    }

    function open(context: ShipmentActivityOpenContext): void {
      messageActionError.value = '';
      stopLiveMessageSubscription();
      stopReferencedFilesSubscription();
      tracking.value = context.tracking;
      record.value = context.record || null;
      closeComposerMenu();
      closeMessageMenu();
      isDragOver.value = false;
      collapseComposer();
      clearEditingState();
      void ensureUserStoreReady();
      void loadReadyFiles();
      startReferencedFilesSubscription(context.tracking);
      startLiveMessageSubscription(context.tracking);
      syncComposerLayoutNextTick();
    }

    function updateRecord(nextRecord: any): void {
      record.value = nextRecord || null;
    }

    function reset(): void {
      messageActionError.value = '';
      activeRequestId += 1;
      clearScheduledComposerLayout();
      stopLiveMessageSubscription();
      stopReferencedFilesSubscription();
      tracking.value = '';
      record.value = null;
      messages.value = [];
      deletingMessageId.value = '';
      editingMessageId.value = '';
      draft.value = '';
      draftAttachments.value = [];
      setUploadingDraftAttachments([]);
      isLoading.value = false;
      isSending.value = false;
      isUploadingAttachments.value = false;
      isDragOver.value = false;
      collapseComposer();
      errorMessage.value = '';
      noticeMessage.value = '';
      closeComposerMenu();
      closeMessageMenu();
      readyFiles.value = [];
      clearAttachmentInput();
      showJumpButton.value = false;
      syncComposerLayoutNextTick();
    }

    function startReferencedFilesSubscription(nextTracking: string): void {
      stopReferencedFilesSubscription();

      if (!nextTracking) {
        referencedFileStateById.value = {};
        hasLoadedReferencedFiles.value = false;
        return;
      }

      referencedFilesUnsubscribe = onSnapshot(
        query(collection(db, 'shipments', nextTracking, 'files'), orderBy('createdAt', 'desc')),
        snapshot => {
          const nextFileStateById: Record<string, { status: string; deleted: boolean }> = {};

          snapshot.docs.forEach(docSnapshot => {
            const nextFile = normalizeSnapshotFile(docSnapshot.id, docSnapshot.data());
            nextFileStateById[nextFile.id] = {
              status: nextFile.status,
              deleted: nextFile.deleted === true
            };
          });

          referencedFileStateById.value = nextFileStateById;
          hasLoadedReferencedFiles.value = true;
        },
        error => {
          console.warn('[ShipmentActivityPane] Failed to load referenced files:', error);
          referencedFileStateById.value = {};
          hasLoadedReferencedFiles.value = false;
        }
      );
    }

    function stopReferencedFilesSubscription(): void {
      if (referencedFilesUnsubscribe) {
        referencedFilesUnsubscribe();
        referencedFilesUnsubscribe = null;
      }

      referencedFileStateById.value = {};
      hasLoadedReferencedFiles.value = false;
    }

    function handleModalShown(): void {
      scheduleComposerLayoutSync();
    }

    const controller: ShipmentActivityController = {
      open,
      updateRecord,
      reset
    };

    onMounted(() => {
      registerShipmentActivityController(controller);
      document.addEventListener('mousedown', handleDocumentPointerDown);
      removeModalShownListener = onDocumentModalEvent('shown.bs.modal', handleModalShown);
      window.addEventListener('resize', handleTimelineScroll);
      void ensureUserStoreReady();
      syncComposerLayoutNextTick();
      (window as Window & { JidokaTooltips?: { refresh: (root?: ParentNode) => void } }).JidokaTooltips?.refresh(
        composerRef.value?.getRootElement() ?? undefined
      );
    });

    onBeforeUnmount(() => {
      stopLiveMessageSubscription();
      stopReferencedFilesSubscription();
      if (window.ShipmentActivityController === controller) {
        delete window.ShipmentActivityController;
      }
      releaseUploadingDraftAttachments(uploadingDraftAttachments.value);
      clearScheduledComposerLayout();
      document.removeEventListener('mousedown', handleDocumentPointerDown);
      removeModalShownListener?.();
      removeModalShownListener = null;
      window.removeEventListener('resize', handleTimelineScroll);
    });

    return {
      activityEmptyLabel,
      activityFilter,
      activityFilterOptions,
      attachmentInputRef,
      attachmentPreviewUrls,
      canShowMessageActions,
      canDeleteMessage,
      canEditMessage,
      canAttachFiles,
      cancelEditingMessage,
      closeMessageMenu,
      composerMenuRenderKey,
      composerMenuOptions,
      composerRef,
      composerHighlightHtml,
      confirmDeleteMessage,
      currentUserAvatar,
      composerSuggestions,
      deletingMessageId,
      draft,
      draftAttachments,
      editingMessageId,
      errorMessage,
      forceComposerExpanded,
      getAnomalyDismissedByName,
      getAnomalyReviewedByLabel,
      getAnomalyPriorityLabel,
      getAnomalyTitle,
      formatAnomalySummaryHtml,
      formatContextMessageHtml,
      formatMessageHtml,
      getAuthorAvatar,
      getAuthorDisplayName,
      getAttachmentPreviewKind,
      getAttachmentPreviewLabel,
      getMessageAttachmentStatusLabel,
      handleAttachmentInputChange,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      handleComposerInput,
      handleComposerFocus,
      handleComposerBlur,
      handleComposerPlusMenuOpen,
      handleComposerMenuSelect,
      handleComposerMentionAction,
      handleComposerPaperclipAction,
      handleComposerShipmentLinkAction,
      handleComposerKeydown,
      handleComposerSelectionChange,
      getMessageMenuOptions,
      handleMessageMenuSelect,
      handleLocationClick,
      handleMenuDelete,
      handleMenuEdit,
      handleTimelineScroll,
      handleTimelineClick,
      handleWaypointEdit,
      highlightedSuggestionIndex,
      isEditing,
      isAnomalyActive,
      isAnomalyAlertMessage,
      isDragOver,
      isLoading,
      isMessageAttachmentRemoved,
      isSending,
      isSendDisabled,
      isUploadingAttachments,
      noticeMessage,
      messageActionError,
      isMessageActionPending,
      messageMenuRenderKey,
      openMessageAttachment,
      applyComposerSuggestion,
      handleComposerMenuUpload,
      removeDraftAttachment,
      scrollToBottom,
      showJumpButton,
      dismissAnomalyAlert,
      dismissingAnomalyMessageId,
      startEditingMessage,
      submitMessage,
      timeDisplayMode,
      timeToggleLabel,
      timeToggleTransitionName,
      toggleTimeDisplayMode,
      tracking,
      timelineRef,
      visibleTimelineItems,
      triggerAttachmentInput,
      uploadingDraftAttachments
    };
  }
});
</script>
